import { ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { signInWithGoogle, signOut } from './auth';
import { Container, Loader, Center, Text, Stack, Button, Group } from '@mantine/core';

interface ProtectedRouteProps {
  children: ReactNode;
  requireRole?: 'Admin' | 'LicenceManager' | 'Viewer';
}

/**
 * Protected Route Component
 * Ensures user is authenticated before rendering children
 * Optionally requires a specific role
 * 
 * @param children - Components to render if authenticated
 * @param requireRole - Optional role requirement (Admin, LicenceManager, or Viewer)
 */
export function ProtectedRoute({ children, requireRole }: ProtectedRouteProps) {
  const { user, loading, error } = useAuth();

  useEffect(() => {
    // If not loading and no user, redirect to sign in
    if (!loading && !user && !error) {
      signInWithGoogle();
    }
  }, [user, loading, error]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <Center style={{ height: '100vh' }}>
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text>Loading...</Text>
        </Stack>
      </Center>
    );
  }

  // Show error state if authentication check failed
  if (error) {
    return (
      <Container size="sm" style={{ marginTop: '2rem' }}>
        <Stack align="center" gap="md">
          <Text c="red" size="lg" fw={600}>
            Authentication Error
          </Text>
          <Text c="dimmed" ta="center">
            {error.message}
          </Text>
          <Group>
            <Button onClick={() => signInWithGoogle()}>
              Try Again
            </Button>
            <Button variant="outline" onClick={() => signOut()}>
              Sign Out
            </Button>
          </Group>
        </Stack>
      </Container>
    );
  }

  // Redirect to sign in if not authenticated
  if (!user) {
    return (
      <Container size="sm" style={{ marginTop: '2rem' }}>
        <Stack align="center" gap="md">
          <Text size="lg">Redirecting to sign in...</Text>
          <Loader size="md" />
        </Stack>
      </Container>
    );
  }

  // Check if user has no role assigned
  if (!user.role) {
    return (
      <Container size="sm" style={{ marginTop: '2rem' }}>
        <Stack align="center" gap="md">
          <Text c="red" size="lg" fw={600}>
            Access Denied
          </Text>
          <Text c="dimmed" ta="center">
            Your account has not been assigned to any user group. 
            Please contact your administrator to grant you access.
          </Text>
          <Button variant="outline" onClick={() => signOut()}>
            Sign Out
          </Button>
        </Stack>
      </Container>
    );
  }

  // Check role requirement if specified
  if (requireRole) {
    const roleHierarchy: Record<string, number> = {
      Admin: 3,
      LicenceManager: 2,
      Viewer: 1,
    };

    const userRoleLevel = roleHierarchy[user.role];
    const requiredRoleLevel = roleHierarchy[requireRole];

    if (userRoleLevel < requiredRoleLevel) {
      return (
        <Container size="sm" style={{ marginTop: '2rem' }}>
          <Stack align="center" gap="md">
            <Text c="red" size="lg" fw={600}>
              Insufficient Permissions
            </Text>
            <Text c="dimmed" ta="center">
              You need {requireRole} role to access this page. 
              Your current role is {user.role}.
            </Text>
            <Button variant="outline" onClick={() => signOut()}>
              Sign Out
            </Button>
          </Stack>
        </Container>
      );
    }
  }

  // User is authenticated and has required role
  return <>{children}</>;
}
